import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Upload, Phone, Mail, CreditCard, Calendar, FileText, Download, Eye, Trash2, CheckCircle, ClipboardCheck } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { toast } from 'sonner';
import { openBlobInNewTab } from '../utils/fileUtils';

const ProfilEvaluateur = ({ user }) => {
  const [profile, setProfile] = useState({
    nom: user.nom || '',
    prenom: user.prenom || '',
    genre: '',
    telephone: user.telephone || '',
    email: user.email || '',
    diplomes: '',
    experience: '',
    numero_carte_pro: '',
    date_expiration_carte: '',
    photo: '',
    signature: '',
  });
  const [documents, setDocuments] = useState([]);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const signatureRef = useRef(null);
  const photoInputRef = useRef(null);

  const GENRES = [
    { value: '', label: 'Selectionner...' },
    { value: 'M.', label: 'Monsieur' },
    { value: 'Mme', label: 'Madame' }
  ];

  const DOCUMENT_TYPES = [
    { type: 'carte_pro', label: 'Carte Professionnelle' },
    { type: 'diplome', label: 'Diplome' },
    { type: 'photo_identite', label: "Photo d'Identite" },
    { type: 'autre', label: 'Autre' }
  ];

  useEffect(() => {
    loadProfile();
    loadDocuments();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await axios.get('/evaluateurs/my-profile');
      if (response.data) {
        setProfile(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
    }
  };

  const loadDocuments = async () => {
    try {
      const response = await axios.get(`/documents/${user.user_id}`);
      setDocuments(response.data || []);
    } catch (error) {
    }
  };

  const handleSave = async () => {
    try {
      await axios.put('/evaluateurs/my-profile', profile);
      toast.success('Profil mis a jour avec succes');
    } catch (error) {
      toast.error('Erreur lors de la mise a jour');
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = async () => {
      const newProfile = { ...profile, photo: reader.result };
      setProfile(newProfile);
      try {
        await axios.put('/evaluateurs/my-profile', newProfile);
        toast.success('Photo mise a jour');
      } catch { toast.error('Erreur'); }
    };
    reader.readAsDataURL(file);
  };

  const handleDocumentUpload = async (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;
    const existingDoc = documents.find(d => d.document_type === docType);
    if (existingDoc) {
      try { await axios.delete(`/documents/${existingDoc.document_id}`); } catch {}
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_id', user.user_id);
    formData.append('document_type', docType);
    try {
      await axios.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploade');
      loadDocuments();
    } catch { toast.error("Erreur lors de l'upload"); }
  };

  const handleViewDocument = async (documentId) => {
    try {
      toast.info('Chargement...');
      const response = await axios.get(`/documents/download/${documentId}`);
      const doc = response.data;
      if (doc.file_data) {
        const byteCharacters = atob(doc.file_data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: doc.content_type || 'application/pdf' });
        openBlobInNewTab(blob);
      }
    } catch { toast.error("Erreur lors de l'ouverture"); }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await axios.delete(`/documents/${documentId}`);
      toast.success('Document supprime');
      loadDocuments();
    } catch { toast.error('Erreur'); }
  };

  const handleDownloadDocument = async (documentId, filename) => {
    try {
      toast.info('Telechargement...');
      const response = await axios.get(`/documents/download/${documentId}`);
      const doc = response.data;
      if (doc.file_data) {
        const byteCharacters = atob(doc.file_data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: doc.content_type || 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.setAttribute('download', filename || doc.filename || 'document');
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
    } catch { toast.error('Erreur'); }
  };

  const handleSaveSignature = async () => {
    if (signatureRef.current.isEmpty()) {
      toast.error('Veuillez dessiner votre signature');
      return;
    }
    const signatureData = signatureRef.current.toDataURL();
    const newProfile = { ...profile, signature: signatureData };
    setProfile(newProfile);
    try {
      await axios.put('/evaluateurs/my-profile', newProfile);
      setShowSignaturePad(false);
      toast.success('Signature enregistree');
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-6" data-testid="profil-evaluateur-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-shrink-0 relative">
            <div className="w-24 h-32 bg-white/20 border-4 border-white/40 rounded-lg overflow-hidden" data-testid="evaluateur-photo-preview">
              {profile.photo ? (
                <img src={profile.photo} alt="Photo" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User className="h-12 w-12 text-white/60" />
                </div>
              )}
            </div>
            <input ref={photoInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
            <button onClick={() => photoInputRef.current?.click()} className="absolute -bottom-2 -right-2 bg-white text-indigo-600 p-2 rounded-full shadow-lg hover:bg-indigo-50 transition-colors">
              <Upload className="h-4 w-4" />
            </button>
          </div>
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="text-white/80 text-sm">{profile.genre || ''}</span>
              <h1 className="text-2xl md:text-3xl font-bold">{profile.prenom || user.prenom} {profile.nom || user.nom}</h1>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/90 mb-3">
              {profile.telephone && <span className="flex items-center gap-1"><Phone className="h-4 w-4" />{profile.telephone}</span>}
              <span className="flex items-center gap-1"><Mail className="h-4 w-4" />{profile.email || user.email}</span>
            </div>
            <div className="bg-white/10 rounded-lg px-4 py-3">
              <p className="font-semibold flex items-center gap-2 mb-1">
                <CreditCard className="h-5 w-5" />
                {profile.numero_carte_pro ? `Carte Pro: ${profile.numero_carte_pro}` : 'N carte pro non renseigne'}
              </p>
              {profile.date_expiration_carte && <p className="text-sm text-white/80">Expiration: {profile.date_expiration_carte}</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Informations personnelles */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4">Informations personnelles</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Genre</Label>
            <select value={profile.genre || ''} onChange={(e) => setProfile({ ...profile, genre: e.target.value })} className="w-full h-10 px-3 text-sm border border-slate-200 rounded-md" data-testid="eval-select-genre">
              {GENRES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
          </div>
          <div className="space-y-2">
            <Label>Prenom</Label>
            <Input value={profile.prenom} onChange={(e) => setProfile({ ...profile, prenom: e.target.value })} data-testid="eval-input-prenom" />
          </div>
          <div className="space-y-2">
            <Label>Nom</Label>
            <Input value={profile.nom} onChange={(e) => setProfile({ ...profile, nom: e.target.value })} data-testid="eval-input-nom" />
          </div>
          <div className="space-y-2">
            <Label>Telephone</Label>
            <Input value={profile.telephone} onChange={(e) => setProfile({ ...profile, telephone: e.target.value })} data-testid="eval-input-telephone" />
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input type="email" value={profile.email} disabled className="bg-slate-50" data-testid="eval-input-email" />
          </div>
        </div>
      </div>

      {/* Carte professionnelle */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-indigo-500" /> Carte Professionnelle
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Numero de carte professionnelle</Label>
            <Input value={profile.numero_carte_pro} onChange={(e) => setProfile({ ...profile, numero_carte_pro: e.target.value })} placeholder="N de carte" data-testid="eval-input-carte-pro" />
          </div>
          <div className="space-y-2">
            <Label>Date d'expiration</Label>
            <Input type="date" value={profile.date_expiration_carte} onChange={(e) => setProfile({ ...profile, date_expiration_carte: e.target.value })} data-testid="eval-input-expiration" />
          </div>
        </div>
      </div>

      {/* Diplomes & Experience */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4">Diplomes et Experience</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Diplomes</Label>
            <textarea value={profile.diplomes || ''} onChange={(e) => setProfile({ ...profile, diplomes: e.target.value })} placeholder="Vos diplomes..." className="w-full min-h-[80px] px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" data-testid="eval-input-diplomes" />
          </div>
          <div className="space-y-2">
            <Label>Experience</Label>
            <textarea value={profile.experience || ''} onChange={(e) => setProfile({ ...profile, experience: e.target.value })} placeholder="Votre experience..." className="w-full min-h-[80px] px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" data-testid="eval-input-experience" />
          </div>
        </div>
      </div>

      {/* Documents */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="h-5 w-5 text-indigo-500" />
          <h2 className="text-xl font-display font-semibold text-[#0F172A]">Mes Documents</h2>
        </div>
        <div className="overflow-hidden border border-slate-200 rounded-lg" data-testid="evaluateur-documents-table">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase">Type</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase">Fichier</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {DOCUMENT_TYPES.map((docType) => {
                const existingDoc = documents.find(d => d.document_type === docType.type);
                return (
                  <tr key={docType.type} className={existingDoc ? 'bg-green-50/30' : ''} data-testid={`eval-doc-row-${docType.type}`}>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-slate-700">{docType.label}</span>
                    </td>
                    <td className="px-4 py-3">
                      {existingDoc ? (
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm text-slate-700 truncate max-w-[200px]">{existingDoc.filename}</p>
                            {existingDoc.uploaded_at && <p className="text-xs text-slate-400">{new Date(existingDoc.uploaded_at).toLocaleDateString('fr-FR')}</p>}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-slate-400 italic">Non televerse</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {existingDoc && (
                          <>
                            <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => handleViewDocument(existingDoc.document_id)} title="Voir"><Eye className="h-3.5 w-3.5" /></Button>
                            <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => handleDownloadDocument(existingDoc.document_id, existingDoc.filename)} title="Telecharger"><Download className="h-3.5 w-3.5" /></Button>
                            <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDeleteDocument(existingDoc.document_id)} title="Supprimer"><Trash2 className="h-3.5 w-3.5" /></Button>
                          </>
                        )}
                        <div className="relative">
                          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleDocumentUpload(e, docType.type)} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-testid={`eval-doc-input-${docType.type}`} />
                          <Button size="sm" className={`h-7 pointer-events-none ${existingDoc ? 'bg-slate-500 hover:bg-slate-600' : 'bg-indigo-500 hover:bg-indigo-600'} text-white`}>
                            <Upload className="h-3.5 w-3.5 mr-1" />
                            {existingDoc ? 'Remplacer' : 'Televerser'}
                          </Button>
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Signature */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4">Signature</h2>
        <p className="text-sm text-slate-600 mb-4">Votre signature sera utilisee sur les documents officiels.</p>
        {!showSignaturePad && profile.signature ? (
          <div>
            <div className="border border-slate-200 rounded-lg p-4 inline-block bg-white" data-testid="eval-saved-signature">
              <img src={profile.signature} alt="Signature" className="h-24" />
            </div>
            <div className="mt-4">
              <Button onClick={() => setShowSignaturePad(true)} variant="outline" data-testid="eval-change-signature">Modifier la signature</Button>
            </div>
          </div>
        ) : (
          <div>
            <div className="border-2 border-slate-200 rounded-lg overflow-hidden" data-testid="eval-signature-canvas">
              <SignatureCanvas ref={signatureRef} canvasProps={{ className: 'w-full h-48 bg-white' }} />
            </div>
            <div className="flex space-x-3 mt-4">
              <Button onClick={handleSaveSignature} className="bg-indigo-500 hover:bg-indigo-600" data-testid="eval-save-signature">Enregistrer</Button>
              <Button onClick={() => signatureRef.current.clear()} variant="outline">Effacer</Button>
              {profile.signature && <Button onClick={() => setShowSignaturePad(false)} variant="outline">Annuler</Button>}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <Button onClick={handleSave} className="bg-indigo-500 hover:bg-indigo-600 text-white px-8" data-testid="eval-save-profile">
          Enregistrer les modifications
        </Button>
      </div>
    </div>
  );
};

export default ProfilEvaluateur;
